"use client";

import { useTranslations } from "next-intl";
import { use } from "react";
import { Image, SRCImage } from "react-datocms";

import { ClockIcon } from "~/components/icons/clock-icon";
import { ExternalLinkIcon } from "~/components/icons/external-link";
import { GlobeIcon } from "~/components/icons/globe-icon";
import { QuestionMarkIcon } from "~/components/icons/question-mark";
import { StructuredText } from "~/components/structured-text";
import { Large } from "~/components/typography";
import { Link } from "~/navigation";

import type { PlaceCardProps } from "../_lib/gql";
import { Context } from "./context";
import { useSectionObserver } from "./table-of-contents";

export function PlaceCard({
  place,
  headline,
  placeDescription,
}: PlaceCardProps) {
  const t = useTranslations("Guides");
  const { placeNodes } = use(Context);
  const googlePlace = placeNodes.find(
    ({ node }) => node.place.id === place.id,
  )?.googlePlace;
  const ref = useSectionObserver({
    node: { place, placeDescription, headline: null },
    googlePlace,
  });

  return (
    <div
      className="my-6 flex flex-col gap-4 md:my-12 md:flex-row-reverse"
      ref={ref}
      id={place.id}
    >
      <div className="shrink-0 basis-1/3">
        <SRCImage
          data={place.heroImage.responsiveImage}
          imgClassName="rounded-xl"
          pictureClassName="hidden md:block"
        />
        <Image
          data={place.heroImage.mobileResponseImage}
          className="w-full rounded-xl md:hidden"
          layout="responsive"
        />
      </div>
      <div className="grow space-y-4">
        <div>
          {headline ? (
            <h2 className="my-0!">{headline}</h2>
          ) : (
            <Large className="mt-0" asChild>
              <div>{place.title}</div>
            </Large>
          )}
          <div className="inline-flex items-center gap-4">
            <Link
              className="text-prune-500 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium"
              target="_blank"
              href={`/places/${place.slug}/${place.id}`}
            >
              {t("learnMore")} <ExternalLinkIcon />
            </Link>
            {googlePlace?.googleMapsUri ? (
              <Link
                className="text-prune-500 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium"
                target="_blank"
                href={googlePlace.googleMapsUri}
              >
                {t("googleMaps")} <ExternalLinkIcon />
              </Link>
            ) : null}
          </div>
          {googlePlace ? (
            <div>
              {googlePlace.formattedAddress ? (
                <div className="mt-2 text-sm text-neutral-500">
                  {googlePlace.formattedAddress}
                </div>
              ) : null}
              <div className="mt-4 text-sm">
                <div className="grid grid-cols-[minmax(25%,auto)_1fr] items-start gap-4">
                  {googlePlace.regularOpeningHours ? (
                    <>
                      <div className="flex items-center gap-2 text-neutral-500">
                        <ClockIcon className="size-6" />
                        <div>{t("hours")}</div>
                      </div>
                      <div>
                        {googlePlace.regularOpeningHours.weekdayDescriptions.map(
                          (day) => (
                            <div key={day}>{day}</div>
                          ),
                        )}
                      </div>
                    </>
                  ) : null}
                  {googlePlace.internationalPhoneNumber ? (
                    <>
                      <div className="flex items-center gap-2 text-neutral-500">
                        <QuestionMarkIcon className="size-6" />
                        <div>{t("phone")}</div>
                      </div>
                      <a
                        href={`tel:${googlePlace.internationalPhoneNumber}`}
                        className="underline"
                      >
                        {googlePlace.internationalPhoneNumber}
                      </a>
                    </>
                  ) : null}
                  {googlePlace.websiteUri ? (
                    <>
                      <div className="flex items-center gap-2 text-neutral-500">
                        <GlobeIcon className="size-6" />
                        <div>{t("website")}</div>
                      </div>
                      <a
                        href={googlePlace.websiteUri}
                        target="_blank"
                        className="underline"
                        rel="noreferrer"
                      >
                        {new URL(googlePlace.websiteUri).hostname}
                      </a>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {placeDescription ? (
          <StructuredText data={{ ...placeDescription, links: [] }} />
        ) : null}
      </div>
    </div>
  );
}
